import React, { useCallback } from 'react';
import { Button, Popover, useToasts } from 'freemium-ui';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import {
  ConfirmationType,
  ContactFormType,
  CustomFieldType,
  FormServFieldType,
} from '../../../../../../shared/types';
import Icon from '../../../../../../shared/components/Icon';
import Reorder from '../../../../../../shared/components/Reorder';
import {
  BACK_END_FIELDS,
  FREEMIUM_DROPDOWN_TYPES,
  HIDE_SETTINGS_CUSTOM_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
  fieldTypes,
} from '../../../../../../shared/constants';
import {
  useDeleteContactFormField,
  useSaveContactForm,
} from '../../../../../../shared/hooks';
import {
  FormContainer,
  FormContent,
  FormHeader,
} from '../../../../../../shared/styles';
import { getText } from '../../../../../../shared/text';
import FieldDisplay from '../components/FieldDisplay';
import FieldEdit from '../components/FieldEdit';
import {
  UUID_LENGTH_DD_CHOICES_FORMSERV,
  getCustomFieldsFilter,
  removeFieldFromHidden,
} from '../utils';
import { CustomFieldList, NewFieldAddContainer } from '../styles';
import { CUSTOM_FIELDS_LIMIT, defaultField } from '../constants';

const {
  settings: { customfields },
} = getText('EN');

type CustomFieldsProps = {
  contactForm: ContactFormType;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

const CustomFields = (props: CustomFieldsProps) => {
  const { t } = useTranslation();
  const { contactForm = {}, setConfirmationData } = props;
  const { account_id, form_id } = contactForm;
  const [, setToast] = useToasts();

  const formRef: React.MutableRefObject<HTMLDivElement | undefined> =
    React.useRef();

  const [localFields, setLocalFields] = React.useState<FormServFieldType[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const [editId, setEditId] = React.useState<string>('');

  const { mutate: saveContactForm, isLoading: isSavingContactForm } =
    useSaveContactForm({ setToast });

  const { mutate: deleteContactFormField, isLoading: isDeletingContactField } =
    useDeleteContactFormField({
      setToast,
      callback: (
        isSuccess: boolean,
        _response: any,
        fieldPayload: FormServFieldType,
      ) => {
        if (isSuccess) {
          removeFieldFromHidden(fieldPayload);
          setConfirmationData({});
        }
      },
    });

  const onAddNewField = (fieldType: CustomFieldType) => {
    const newField: FormServFieldType = {
      ...defaultField,
      ...(fieldType.metaData || {}),
      account_id,
      form_id,
      id: uuid(),
      position: (contactForm?.fields?.length || 0) + 1,
      // label: `${customfields?.fields?.newField?.name} ${fieldType.name}`,
      type: fieldType.type,
    };
    if (FREEMIUM_DROPDOWN_TYPES.includes(fieldType.type)) {
      newField.choices = [
        {
          id: uuid().slice(0, UUID_LENGTH_DD_CHOICES_FORMSERV),
          value: `${t(`${customfields?.fields?.newOption?.name}`)}`,
        },
      ];
    }
    setLocalFields((oldFields: FormServFieldType[]) => [
      ...oldFields,
      newField,
    ]);
    setAnchorEl(null);
    setTimeout(() => {
      if (formRef && formRef.current)
        formRef.current.scrollTop = formRef.current.scrollHeight;
    }, 100);
    setEditId(newField.id);
  };

  const proceedWithDelete = useCallback(
    (fieldToDelete: FormServFieldType) => {
      if (fieldToDelete.created_at) deleteContactFormField(fieldToDelete);
      else {
        setLocalFields((oldFields: FormServFieldType[]) =>
          oldFields.filter((o) => o.id !== fieldToDelete.id),
        );
        setConfirmationData({});
      }
    },
    [deleteContactFormField, setConfirmationData],
  );

  const onDeleteField = useCallback(
    (fieldToDelete: FormServFieldType, skipConfirmation?: boolean) => {
      if (skipConfirmation) {
        proceedWithDelete(fieldToDelete);
      } else
        setConfirmationData({
          id: new Date().toISOString(),
          title: `${t('confirm_deletion')}`,
          onConfirm: () => {
            setConfirmationData((old) => ({
              ...old,
              confirmButtonDisabled: true,
            }));
            proceedWithDelete(fieldToDelete);
          },
          confirmButtonText: `${t('delete')}`,
          className: 'error',
          fill: '#d7461f',
          infoText: `${t('settings.custom_fields.delete_field_warning_text')}`,
          description: `${t('settings.custom_fields.delete_confirmation_text', {
            label: fieldToDelete?.label,
          })}?`,
          glyph: 'info',
        });
    },
    [proceedWithDelete, setConfirmationData, t],
  );

  React.useEffect(() => {
    setLocalFields(
      (contactForm?.fields || []).filter(
        (field: FormServFieldType) =>
          field.visible &&
          ![
            ...TEMPORARY_HIDDEN_FIELDS,
            ...BACK_END_FIELDS,
            ...HIDE_SETTINGS_CUSTOM_FIELDS,
          ].includes(field.name),
      ),
    );
  }, [contactForm]);

  const isLoading = isDeletingContactField || isSavingContactForm;

  const currentCount = React.useMemo<Record<string, number>>(() => {
    const cCount: Record<string, number> = {};
    localFields.forEach((field) => {
      cCount[field.type] = (cCount[field.type] || 0) + 1;
    });
    return cCount;
  }, [localFields]);

  const handleReorder = (reorderedFields: FormServFieldType[]) => {
    const oldFields = contactForm.fields || [];
    const newFields = uniqBy([...reorderedFields, ...oldFields], 'name');
    saveContactForm({
      ...contactForm,
      fields: newFields,
    });
  };

  const renderCustomFields = useCallback(
    (field: FormServFieldType) =>
      editId === field.id ? (
        <FieldEdit
          field={field}
          fields={localFields}
          setEditId={setEditId}
          isLoading={isLoading}
          removeFieldFromHidden={removeFieldFromHidden}
          onDeleteField={onDeleteField}
        />
      ) : (
        <FieldDisplay
          field={field}
          editId={editId}
          setEditId={setEditId}
          isLoading={isLoading}
          onDeleteField={onDeleteField}
        />
      ),
    [editId, isLoading, localFields, onDeleteField],
  );

  return (
    <FormContainer>
      <FormHeader hideBorder>
        <div className="left-pane">
          <div>
            <div className="heading">{`${t(customfields?.layout?.heading)} ${
              localFields.filter(getCustomFieldsFilter).length
            }/${CUSTOM_FIELDS_LIMIT}`}</div>
            <div className="sub-heading">
              {t(customfields?.layout?.description)}
            </div>
          </div>
        </div>
        <div className="right-pane">
          <Button
            type="secondary"
            inline
            disabled={
              localFields.filter(getCustomFieldsFilter).length >=
                CUSTOM_FIELDS_LIMIT || Boolean(editId)
            }
            aria-describedby="custom-field-add"
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              setAnchorEl(event.currentTarget);
            }}
          >
            {`+ ${t('settings.add_field')}`}
          </Button>
          <Popover
            id="custom-field-add"
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <NewFieldAddContainer>
              {fieldTypes.map((fieldType: CustomFieldType) => (
                <div
                  className={`field-card ${
                    (currentCount[fieldType.type] || 0) >= fieldType.limit
                      ? 'disabled'
                      : ''
                  }`.trim()}
                  key={fieldType.id}
                  onClick={(event: React.MouseEvent) => {
                    event.preventDefault();
                    if ((currentCount[fieldType.type] || 0) >= fieldType.limit)
                      return;
                    onAddNewField(fieldType);
                  }}
                >
                  <Icon
                    width={fieldType.width}
                    height={fieldType.height}
                    glyph={fieldType.glyph}
                  />
                  <div className="field-name">{t(fieldType.name)}</div>
                </div>
              ))}
            </NewFieldAddContainer>
          </Popover>
        </div>
      </FormHeader>
      <FormContent ref={formRef}>
        <CustomFieldList id="CustomFieldList">
          <Reorder
            onReorder={handleReorder}
            // idIdentifier="id"
            disabled={Boolean(editId)}
            customRender={renderCustomFields}
            items={localFields}
          />
        </CustomFieldList>
      </FormContent>
    </FormContainer>
  );
};

export default CustomFields;
